import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";

import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";
import type { ChatUIMessage } from "components/AssistantPanel/Chat/types";

import { CHAT_CONVERSATION } from "./gql";

const useChatConversation = () => {
  const [fetchConversation, { loading, error }] = useLazyQuery(CHAT_CONVERSATION, {
    fetchPolicy: "network-only",
  });

  const loadConversation = useCallback(
    async (
      id: string
    ): Promise<{
      messages: ChatUIMessage[];
      version: number;
      integrationId: string | undefined;
      model: string | undefined;
    } | null> => {
      let data;
      try {
        ({ data } = await fetchConversation({ variables: { id } }));
      } catch (error) {
        rethrowIfNotAborted(error as Error);
        return null;
      }

      if (!data?.intentChatConversation) {
        return null;
      }

      const messages = data.intentChatConversation.messages.reduce<ChatUIMessage[]>((acc, msg) => {
        try {
          acc.push(JSON.parse(msg.content) as ChatUIMessage);
        } catch {
          // Skip malformed messages to avoid breaking the entire conversation
        }
        return acc;
      }, []);

      return {
        messages,
        version: data.intentChatConversation.version,
        integrationId: data.intentChatConversation.lastUsedAIIntegration?.id,
        model: data.intentChatConversation.lastUsedAIModel ?? undefined,
      };
    },
    [fetchConversation]
  );

  return {
    loadConversation,
    loading,
    error,
  };
};

export default useChatConversation;
